import { GLOBAL_THEME_DEFAULT } from '_constants';
import { parseDateFromText } from './dateParsing';

// organizationId is passed through so every Person carries a worksFor reference.
export const personToJsonLd = (person: Content.Person, organizationId?: string): Record<string, unknown> | null => {
	if (!person?.properties) return null;

	// linkedIn's type says { url }, but the CMS field is a plain string - handle both.
	const linkedIn =
		typeof person.properties.linkedIn === 'string' ? person.properties.linkedIn : (person.properties.linkedIn as unknown as Content.Link)?.url;

	return {
		'@type': 'Person',
		name: person.properties.fullName || undefined,
		jobTitle: person.properties.workTitle || undefined,
		description: person.properties.bio || undefined,
		email: person.properties.email || undefined,
		telephone: person.properties.phoneNumber || undefined,
		image: person.properties.image?.url || undefined,
		sameAs: linkedIn ? [linkedIn] : undefined,
		worksFor: organizationId ? { '@id': organizationId } : undefined,
	};
};

// schema.org's areaServed wants a country/region code, not a full IETF locale tag - "da-DK" -> "DK".
export const deriveAreaServed = (culture: string | undefined): string[] | undefined => {
	const region = culture?.split('-')[1];
	return region ? [region] : undefined;
};

export const imageToJsonLd = (image: Content.Image | undefined): Record<string, unknown> | undefined =>
	image?.url
		? {
				'@type': 'ImageObject',
				url: image.url,
				width: image.properties?.umbracoWidth ? Number(image.properties.umbracoWidth) : undefined,
				height: image.properties?.umbracoHeight ? Number(image.properties.umbracoHeight) : undefined,
		  }
		: undefined;

// Fallback hero image/heading, used when a page-specific field is empty.
export const findPageHeaderHero = (pageHeader: Content.HeroCard[] | undefined): Content.HeroCard['content']['properties'] | undefined =>
	pageHeader?.find((block) => block.documentType === 'heroCard')?.content?.properties;

export const addressToJsonLd = (address: Content.PostalAddress): Record<string, unknown> => ({
	'@type': 'PostalAddress',
	streetAddress: address.streetAddress || undefined,
	addressLocality: address.addressLocality || undefined,
	postalCode: address.postalCode || undefined,
	addressCountry: address.addressCountry || undefined,
});

// Per-theme brand asset. Not the SVGs Logo.tsx uses - those import as components, not URLs.
const LOGO_PATH_BY_THEME: Record<string, string> = {
	kogp: '/kogp/android-chrome-512x512.png',
	'pentia-dk': '/android-chrome-256x256.png',
	'pentia-se': '/android-chrome-256x256.png',
};

export const resolveLogoUrl = (siteUrl: string | undefined, themeSlug: string | undefined): string | undefined => {
	if (!siteUrl) return undefined;
	const logoPath = LOGO_PATH_BY_THEME[themeSlug] ?? LOGO_PATH_BY_THEME[GLOBAL_THEME_DEFAULT];
	return `${siteUrl.replace(/\/$/, '')}${logoPath}`;
};

// No date field for Event layouts - scans articleInfo free text for a parseable one instead.
export const findEventStartDate = (articleInfo: Content.ArticleInfoColumn[] | undefined): string | undefined => {
	for (const column of articleInfo ?? []) {
		const props = column?.content?.properties;
		const found = parseDateFromText(props?.line) ?? parseDateFromText(props?.line2) ?? parseDateFromText(props?.heading);
		if (found) return found;
	}
	return undefined;
};

// Flattens pageSections' nested module list so scanners don't each rewrite the same two loops.
function* iterateModules(pageSections: Content.ContentSection[] | undefined) {
	for (const section of pageSections ?? []) {
		yield* section?.content?.properties?.content ?? [];
	}
}

// No dedicated "team" content type - scans for employeeList (several people) and contactPerson
// (one) blocks wherever they occur, deduping by email.
export const findTeamPersons = (pageSections: Content.ContentSection[] | undefined): Content.Person[] => {
	const seen = new Set<string>();
	const persons: Content.Person[] = [];

	const add = (person: Content.Person | undefined) => {
		if (!person?.properties) return;
		const key = person.properties.email || person.properties.fullName;
		if (!key || seen.has(key)) return;
		seen.add(key);
		persons.push(person);
	};

	for (const module of iterateModules(pageSections)) {
		if (module.documentType === 'employeeList') {
			(module.content?.properties?.employees ?? []).forEach(add);
		}
		if (module.documentType === 'contactPerson') {
			add(module.content?.properties?.include_ref_author);
		}
	}

	return persons;
};

// Stable per-person @id, same idea as Organization's own "#organization".
export const personIdSlug = (person: Content.Person): string =>
	(person.properties?.fullName || '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/(^-|-$)/g, '');

const stripHtml = (html: string | null | undefined): string => (html ?? '').replace(/<[^>]+>/g, '').trim();

// Only accordions flagged isFaq count - Google warns against marking up generic collapsible
// content as FAQPage. Merges every flagged accordion, since Google wants one FAQPage per page.
export const findFaqItems = (pageSections: Content.ContentSection[] | undefined): Record<string, unknown>[] => {
	const questions: Record<string, unknown>[] = [];

	for (const module of iterateModules(pageSections)) {
		if (module.documentType !== 'accordion' || !module.content?.properties?.isFaq) continue;

		for (const item of module.content.properties.items ?? []) {
			const heading = item?.content?.properties?.heading;
			const text = stripHtml(item?.content?.properties?.text);
			if (!heading || !text) continue;
			questions.push({
				'@type': 'Question',
				name: heading,
				acceptedAnswer: { '@type': 'Answer', text },
			});
		}
	}

	return questions;
};
